/* ---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 * --------------------------------------------------------------------------------------------*/

import {
  Button,
  Modal,
  ModalBody,
  ModalHeader
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
  ILightboxConfig,
  ILightboxProps
} from './lightbox.props.autogenerated';

interface ILightBoxState extends React.ComponentState {
  isOpen: boolean;
}

/**
 *
 * Lightbox component
 * @extends {React.PureComponent<ILightboxProps<ILightboxConfig>>}
 */
class Lightbox extends React.PureComponent<
  ILightboxProps<ILightboxConfig>,
  ILightBoxState
> {
  constructor(props: ILightboxProps<ILightboxConfig>) {
    super(props);
    this.state = { isOpen: false };
    this._closeModal = this._closeModal.bind(this);
    this._openModal = this._openModal.bind(this);
  }

  public componentDidMount(): void {
    const {
      config: { defaultOpenState }
    } = this.props;
    if (defaultOpenState) {
      this.setState({ isOpen: defaultOpenState });
    }
  }

  public render(): JSX.Element {
    const { config } = this.props;
    const launchLightboxButton = this._launchLightboxButton(
      config.hasLaunchLightboxButton,
      config.launchLightboxButtonText
    );

    return (
      <div
        className={
          config.className && config.className !== ''
            ? config.className
            : 'lightbox'
        }
      >
        {launchLightboxButton}
        <Modal
          autoFocus={config.autoFocus}
          fade={config.fade}
          isOpen={this.state.isOpen}
          horizontalPosition={config.horizontalPosition}
          verticalPosition={config.verticalPosition}
          zIndex={config.zIndex}
          toggle={this._closeModal}
          applicationNode={'rsg-root'}
          aria-labelledby=''
        >
          <ModalHeader toggle={this._closeModal}>
            {config.modalHeader}
          </ModalHeader>
          <ModalBody>
            {this.props.slots &&
            this.props.slots.content &&
            this.props.slots.content.length
              ? this.props.slots.content.map(
                  (slot: React.ReactNode, index: number) => {
                    return <div key={index}>{slot}</div>;
                  }
                )
              : false}
          </ModalBody>
        </Modal>
      </div>
    );
  }

  private _launchLightboxButton(
    hasLaunchLightboxButton: boolean | undefined,
    buttonText: string | undefined
  ): JSX.Element | null {
    if (hasLaunchLightboxButton) {
      return (
        <Button className='launch-lightbox-button' onClick={this._openModal}>
          {buttonText}
        </Button>
      );
    }
    return null;
  }

  private _closeModal(): void {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen
    }));
  }
  private _openModal(): void {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen
    }));
  }
}

export default Lightbox;
